import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  TrendingUp, 
  TrendingDown, 
  Target, 
  DollarSign,
  BarChart3,
  Activity,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import Navigation from './Navigation';
import toast from 'react-hot-toast';
import moment from 'moment';

const BetDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [position, setPosition] = useState(null);
  const [pnlHistory, setPnlHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [realTimePrice, setRealTimePrice] = useState(null);

  const fetchPositionDetails = async () => {
    try {
      const response = await fetch(`/api/trading/position/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPosition(data.position);
        setPnlHistory(data.pnlHistory || generateMockPnlHistory(data.position));
        setRealTimePrice(data.currentPrice);
      } else {
        toast.error('Position not found');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to fetch position details:', error);
      toast.error('Failed to load position details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchPositionDetails();
      // Set up real-time updates
      const interval = setInterval(fetchPositionDetails, 5000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const generateMockPnlHistory = (pos) => {
    if (!pos) return [];
    
    const startTime = moment(pos.created_at);
    const now = moment();
    const duration = now.diff(startTime, 'minutes');
    const points = Math.min(50, Math.max(10, duration));
    
    const history = [];
    const currentPnl = pos.unrealized_pnl || 0;
    
    for (let i = 0; i <= points; i++) {
      const time = startTime.clone().add((duration * i) / points, 'minutes');
      const volatility = 0.3;
      const trend = currentPnl / points;
      const noise = (Math.random() - 0.5) * volatility * Math.abs(currentPnl || 100);
      const pnl = (trend * i) + noise;
      
      history.push({
        time: time.format('HH:mm'),
        timestamp: time.valueOf(),
        pnl: parseFloat(pnl.toFixed(2)),
        price: pos.entry_price + (pnl / pos.position_size),
        cumulative: pnl
      });
    }
    
    return history;
  };

  const handleClosePosition = async () => {
    try {
      toast.loading('Closing position...', { id: 'closing' });
      
      const response = await fetch('/api/trading/close-position', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ positionId: id })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Position closed! ${result.message}`, { id: 'closing' });
        navigate('/dashboard');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to close position', { id: 'closing' });
      }
    } catch (error) {
      toast.error('Error closing position', { id: 'closing' });
      console.error('Close position error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900">
        <Navigation />
        <div className="container mx-auto px-4 py-8 flex items-center justify-center">
          <div className="casino-card p-8 text-center">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-white">Loading position details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!position) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900">
        <Navigation />
        <div className="container mx-auto px-4 py-8 flex items-center justify-center">
          <div className="casino-card p-8 text-center">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Position Not Found</h2>
            <p className="text-gray-400 mb-6">The requested position could not be found.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-3 px-6 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const strategyDetails = position.strategy_details ? JSON.parse(position.strategy_details) : {};
  const isProfit = (position.unrealized_pnl || 0) >= 0;
  const pnlPercentage = ((position.unrealized_pnl || 0) / position.bet_amount) * 100;
  const timeLeft = moment(position.expires_at).diff(moment(), 'minutes');
  const duration = moment().diff(moment(position.created_at), 'minutes');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center">
                {position.direction > 0 ? (
                  <TrendingUp className="w-8 h-8 text-green-400 mr-3" />
                ) : (
                  <TrendingDown className="w-8 h-8 text-red-400 mr-3" />
                )}
                {position.symbol} Position
              </h1>
              <p className="text-gray-400">
                {position.direction > 0 ? 'Long' : 'Short'} • {position.instrument_type} • 
                {position.leverage}x Leverage
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className={`px-4 py-2 rounded-lg ${isProfit ? 'bg-green-900 border border-green-600' : 'bg-red-900 border border-red-600'}`}>
              <div className="text-center">
                <div className={`text-2xl font-bold ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
                  {isProfit ? '+' : ''}${(position.unrealized_pnl || 0).toFixed(2)}
                </div>
                <div className={`text-sm ${isProfit ? 'text-green-300' : 'text-red-300'}`}>
                  {isProfit ? '+' : ''}{pnlPercentage.toFixed(2)}%
                </div>
              </div>
            </div>
            
            {position.status === 'active' && (
              <button
                onClick={handleClosePosition}
                className="bg-gradient-to-r from-red-600 to-red-700 text-white font-bold py-3 px-6 rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200"
              >
                Close Position
              </button>
            )}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* P&L Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="casino-card p-6"
            >
              <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-blue-400" />
                Profit & Loss Chart
              </h2>
              
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={pnlHistory}>
                    <defs>
                      <linearGradient id="pnlGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={isProfit ? "#10b981" : "#ef4444"} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={isProfit ? "#10b981" : "#ef4444"} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="time" 
                      stroke="#9ca3af"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#9ca3af"
                      fontSize={12}
                      tickFormatter={(value) => `$${value.toFixed(0)}`}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#1f2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#f3f4f6'
                      }}
                      formatter={(value) => [`$${value.toFixed(2)}`, 'P&L']}
                    />
                    <Area
                      type="monotone"
                      dataKey="pnl"
                      stroke={isProfit ? "#10b981" : "#ef4444"}
                      strokeWidth={2}
                      fill="url(#pnlGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            {/* Trade Data Table */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              delay={0.1}
              className="casino-card p-6"
            >
              <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                <Activity className="w-5 h-5 mr-2 text-green-400" />
                Professional Trade Data
              </h2>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Metric</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Value</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody className="text-white">
                    <tr className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="py-3 px-4 font-medium">Entry Price</td>
                      <td className="py-3 px-4">${position.entry_price?.toFixed(4)}</td>
                      <td className="py-3 px-4 text-gray-400">Initial execution price</td>
                    </tr>
                    <tr className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="py-3 px-4 font-medium">Current Price</td>
                      <td className="py-3 px-4">${(realTimePrice || position.current_price || position.entry_price)?.toFixed(4)}</td>
                      <td className="py-3 px-4 text-gray-400">Live market price</td>
                    </tr>
                    <tr className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="py-3 px-4 font-medium">Position Size</td>
                      <td className="py-3 px-4">{position.position_size?.toFixed(4)} units</td>
                      <td className="py-3 px-4 text-gray-400">Total position exposure</td>
                    </tr>
                    <tr className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="py-3 px-4 font-medium">Leverage</td>
                      <td className="py-3 px-4">{position.leverage?.toFixed(2)}x</td>
                      <td className="py-3 px-4 text-gray-400">Applied leverage multiplier</td>
                    </tr>
                    <tr className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="py-3 px-4 font-medium">Stop Loss</td>
                      <td className="py-3 px-4">${position.stop_loss?.toFixed(2)}</td>
                      <td className="py-3 px-4 text-gray-400">Maximum loss threshold</td>
                    </tr>
                    <tr className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="py-3 px-4 font-medium">Take Profit</td>
                      <td className="py-3 px-4">${position.take_profit?.toFixed(2)}</td>
                      <td className="py-3 px-4 text-gray-400">Target profit level</td>
                    </tr>
                    <tr className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="py-3 px-4 font-medium">Duration</td>
                      <td className="py-3 px-4">{duration} minutes</td>
                      <td className="py-3 px-4 text-gray-400">Time since opening</td>
                    </tr>
                    <tr className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="py-3 px-4 font-medium">Time Left</td>
                      <td className="py-3 px-4">
                        {timeLeft > 0 ? `${timeLeft} minutes` : 'Expired'}
                      </td>
                      <td className="py-3 px-4 text-gray-400">Until auto-close</td>
                    </tr>
                    <tr className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="py-3 px-4 font-medium">Success Probability</td>
                      <td className="py-3 px-4">{position.success_probability}%</td>
                      <td className="py-3 px-4 text-gray-400">AI-calculated odds</td>
                    </tr>
                    <tr className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="py-3 px-4 font-medium">Risk Score</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          position.risk_score < 0.3 ? 'bg-green-900 text-green-300' :
                          position.risk_score < 0.6 ? 'bg-yellow-900 text-yellow-300' :
                          position.risk_score < 0.8 ? 'bg-orange-900 text-orange-300' :
                          'bg-red-900 text-red-300'
                        }`}>
                          {(position.risk_score * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-400">Risk assessment level</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Position Summary */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="casino-card p-6"
            >
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                <Target className="w-5 h-5 mr-2 text-purple-400" />
                Position Summary
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-400">Bet Amount</span>
                  <span className="text-white font-medium">${position.bet_amount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Target Gain</span>
                  <span className="text-green-400 font-medium">${position.target_gain}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Current P&L</span>
                  <span className={`font-medium ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
                    {isProfit ? '+' : ''}${(position.unrealized_pnl || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Return %</span>
                  <span className={`font-medium ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
                    {isProfit ? '+' : ''}{pnlPercentage.toFixed(2)}%
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Bet Composition */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              delay={0.1}
              className="casino-card p-6"
            >
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                <DollarSign className="w-5 h-5 mr-2 text-yellow-400" />
                Bet Composition
              </h3>
              
              <div className="space-y-3">
                <div className="bg-gray-800 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">Base Asset</span>
                    <span className="text-blue-400">{position.symbol}</span>
                  </div>
                  <div className="text-xs text-gray-400">
                    Primary underlying instrument
                  </div>
                </div>
                
                <div className="bg-gray-800 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">Instrument Type</span>
                    <span className="text-purple-400 capitalize">{position.instrument_type}</span>
                  </div>
                  <div className="text-xs text-gray-400">
                    {position.instrument_type === 'options' ? 'Derivative contract' : 
                     position.instrument_type === 'futures' ? 'Futures contract' : 'Direct equity'}
                  </div>
                </div>
                
                <div className="bg-gray-800 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">Strategy</span>
                    <span className="text-green-400">
                      {position.direction > 0 ? 'Long' : 'Short'} {position.leverage?.toFixed(1)}x
                    </span>
                  </div>
                  <div className="text-xs text-gray-400">
                    {strategyDetails.marketAnalysis?.currentSentiment || 'Market neutral'} bias
                  </div>
                </div>
              </div>
            </motion.div>

            {/* AI Analysis */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              delay={0.2}
              className="casino-card p-6"
            >
              <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                <CheckCircle className="w-5 h-5 mr-2 text-blue-400" />
                AI Analysis
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Confidence</span>
                  <span className="text-blue-400">{(position.ai_confidence * 100).toFixed(0)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Model</span>
                  <span className="text-white">Neural Network v2.1</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Sentiment</span>
                  <span className="text-purple-400">
                    {strategyDetails.marketAnalysis?.currentSentiment || 'Neutral'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Volatility</span>
                  <span className="text-orange-400">
                    {strategyDetails.marketAnalysis?.volatilityRegime || 'Normal'}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BetDetails;
